import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, CheckCircle, Loader2, Lock, ShieldCheck } from 'lucide-react';
import { paymentActionsAPI } from '../services/api';
import { stripeFor } from '../components/payments/CardForm';
import { formatMoney } from '../utils/format';
import { useBrandingStore } from '../context/brandingStore';

/**
 * "Your bank asked you to confirm a payment" (Phase 7d).
 *
 * A charge on a saved card came back from the customer's bank asking for
 * them to authenticate it (3-D Secure). This page lets them do that, with
 * the bank's own check, and then asks the server to look the payment up —
 * the server records it only when the gateway says it went through.
 *
 * Two ways in: `/pay/confirm/:token` from the emailed link (no sign-in; the
 * token names this one payment and expires after 72 hours), and
 * `/payments/confirm/:actionId` for someone already signed in, which Pay Now
 * sends them to straight away.
 *
 * Customer-facing: names no payment processor.
 */
export default function PaymentActionPage({ mode = 'token' }) {
  const params = useParams();
  const byToken = mode === 'token';
  const key = byToken ? params.token : params.actionId;
  const [phase, setPhase] = useState('idle'); // idle | working | done | error
  const [message, setMessage] = useState('');
  const portalName = useBrandingStore((s) => s.branding?.portalName) || null;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['payment-action', mode, key],
    queryFn: () => (byToken ? paymentActionsAPI.getByToken(key) : paymentActionsAPI.get(key)).then((r) => r.data),
    retry: false,
  });

  const complete = () => (byToken ? paymentActionsAPI.completeByToken(key) : paymentActionsAPI.complete(key)).then((r) => r.data);

  const confirm = async () => {
    setPhase('working');
    setMessage('');
    try {
      const stripe = await stripeFor(data.publishableKey, data.stripeAccount);
      if (!stripe) throw new Error('The secure payment form could not be loaded. Please try again.');
      const outcome = data.nextAction
        ? await stripe.handleNextAction({ clientSecret: data.clientSecret })
        : await stripe.confirmCardPayment(data.clientSecret, data.paymentMethodId ? { payment_method: data.paymentMethodId } : undefined);
      if (outcome?.error) {
        setPhase('error');
        setMessage(outcome.error.message || 'Your bank did not confirm the payment.');
        return;
      }
      const result = await complete();
      if (result.status === 'succeeded' || result.status === 'processing') {
        setPhase('done');
      } else {
        setPhase('error');
        setMessage(result.message || 'The payment has not gone through yet. Please try again.');
        refetch();
      }
    } catch (e) {
      setPhase('error');
      setMessage(e.response?.data?.error || e.message || 'Something went wrong. Please try again.');
    }
  };

  const shell = (children) => (
    <div className={byToken ? 'min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center px-4 py-10' : 'flex justify-center px-4 py-10'}>
      <div className="w-full max-w-md bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 space-y-5">
        {portalName && <div className="text-center text-sm font-medium text-neutral-500 dark:text-neutral-400">{portalName}</div>}
        {children}
      </div>
    </div>
  );

  if (isLoading) {
    return shell(
      <div className="flex items-center justify-center gap-2 text-neutral-500 py-8">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading your payment…
      </div>
    );
  }

  if (error || !data) {
    return shell(
      <div className="text-center space-y-3">
        <AlertCircle className="w-10 h-10 text-amber-500 mx-auto" />
        <h1 className="text-lg font-semibold dark:text-white">This link is not valid</h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          {error?.response?.data?.error || 'It may have expired. Contact us and we will send you a new one.'}
        </p>
      </div>
    );
  }

  const amount = formatMoney(data.amount);

  if (phase === 'done' || data.status === 'succeeded') {
    return shell(
      <div className="text-center space-y-3">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
        <h1 className="text-lg font-semibold dark:text-white">Payment confirmed</h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-300">Thank you. Your payment of {amount} has gone through.</p>
        {!byToken && <Link to="/payments" className="btn-primary btn-sm inline-block">Back to payments</Link>}
      </div>
    );
  }

  if (data.status === 'canceled') {
    return shell(
      <div className="text-center space-y-3">
        <AlertCircle className="w-10 h-10 text-neutral-400 mx-auto" />
        <h1 className="text-lg font-semibold dark:text-white">This payment was cancelled</h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-300">Nothing was charged. Contact us if you still want to pay.</p>
      </div>
    );
  }

  if (data.status !== 'requires_action') {
    return shell(
      <div className="text-center space-y-3">
        <AlertCircle className="w-10 h-10 text-amber-500 mx-auto" />
        <h1 className="text-lg font-semibold dark:text-white">We could not load this payment</h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-300">Please try again in a few minutes, or contact us.</p>
      </div>
    );
  }

  return shell(
    <>
      <div className="text-center space-y-2">
        <ShieldCheck className="w-10 h-10 text-primary-600 mx-auto" />
        <h1 className="text-lg font-semibold dark:text-white">Your bank asked you to confirm a payment</h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          This is a normal security check. Nothing has been charged yet.
        </p>
      </div>
      <div className="rounded-lg bg-neutral-50 dark:bg-neutral-700/50 p-4 text-center">
        <div className="text-2xl font-semibold dark:text-white">{amount}</div>
        {data.description && <div className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">{data.description}</div>}
      </div>
      {phase === 'error' && message && (
        <div className="flex gap-2 rounded-lg bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-200">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {message}
        </div>
      )}
      <button
        onClick={confirm}
        disabled={phase === 'working' || !data.clientSecret || !data.publishableKey}
        className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
      >
        {phase === 'working' ? <><Loader2 className="w-4 h-4 animate-spin" /> Waiting for your bank…</> : <><Lock className="w-4 h-4" /> Confirm with my bank</>}
      </button>
      <p className="text-xs text-center text-neutral-500 dark:text-neutral-400">
        Your bank will show its own check — usually a code by text message or a prompt in your banking app. If you did not expect this payment, do not confirm it; contact us instead.
      </p>
    </>
  );
}
